/**
 * Portfolio Info Bar Component
 * Displays aggregated portfolio context (all clients, leads, branches) and applied filter summary
 */

import { Building2, User, Globe, Calendar, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { exportToPDF, generateFilename } from '@/lib/utils/export-utils'
import { useFormatters } from '@/hooks/use-formatters'

interface PortfolioInfoBarProps {
  clientLabel?: string
  leadLabel?: string
  branchLabel?: string
  startDate?: string
  endDate?: string
}

export function PortfolioInfoBar({
  clientLabel = 'All Clients',
  leadLabel = 'All Leads',
  branchLabel = 'All Branches',
  startDate,
  endDate,
}: PortfolioInfoBarProps) {
  const { formatDate } = useFormatters()

  const handleExportPDF = async () => {
    try {
      const filename = generateFilename('PortfolioDashboard', 'all', 'pdf')
      await exportToPDF('portfolio-dashboard', filename)
    } catch (error) {
      console.error('Failed to export PDF:', error)
    }
  }

  const periodLabel =
    startDate && endDate
      ? `${formatDate(startDate)} – ${formatDate(endDate)}`
      : '—'

  return (
    <div className="flex items-center gap-6 px-4 py-2.5 bg-muted/50 rounded-md border flex-wrap">
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Client:</span>
        <span className="text-sm font-medium">{clientLabel}</span>
      </div>

      <div className="h-4 w-px bg-border" />

      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Engagement Lead:</span>
        <span className="text-sm font-medium">{leadLabel}</span>
      </div>

      <div className="h-4 w-px bg-border" />

      <div className="flex items-center gap-2">
        <Globe className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Branch:</span>
        <span className="text-sm font-medium">{branchLabel}</span>
      </div>

      <div className="h-4 w-px bg-border" />

      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Period:</span>
        <span className="text-sm font-medium">{periodLabel}</span>
      </div>

      <div className="flex-1" />

      <Button variant="outline" size="sm" onClick={handleExportPDF}>
        <Download className="w-3.5 h-3.5 mr-1.5" />
        Export PDF
      </Button>
    </div>
  )
}
